import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { getJourneys } from "@/lib/api/quests"
import { useQuestsStore } from "@/lib/store/quests"
import { queryKeys } from "@/lib/utils/query"
import { Journey, JourneysResponse } from "@/types/quests"

export const isMature = (journeys: Journey[]): boolean => {
  if (!journeys || journeys.length === 0) {
    return false
  }

  // Check if any journey is completed
  const hasCompletedJourney = journeys.some((journey) => journey.state === "OK")

  if (hasCompletedJourney) {
    return true
  }

  // Count completed journey missions across all journeys
  const completedMissionsCount = journeys.reduce((count, journey) => {
    return (
      count +
      journey.journey_stages.reduce((stageCount, stage) => {
        return (
          stageCount +
          stage.journey_missions.filter((mission) => mission.state === "OK")
            .length
        )
      }, 0)
    )
  }, 0)

  return completedMissionsCount >= 2
}

export const useJourneys = (isAuthenticated: boolean) => {
  const {
    journeys,
    setJourneys,
    getJourneyById,
    setCurrentJourneyId,
    setCurrentJourney,
    setCurrentMission,
  } = useQuestsStore()

  const {
    data: journeysData,
    isLoading: isLoadingJourneys,
    error: journeysError,
  } = useQuery<JourneysResponse>({
    queryKey: queryKeys.journeys,
    queryFn: getJourneys,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

  useEffect(() => {
    if (journeysData?.results) {
      setJourneys(journeysData.results)
    }
  }, [journeysData, setJourneys])

  return {
    journeys,
    loading: isLoadingJourneys,
    error: journeysError,
    getJourneyById,
    setCurrentJourneyId,
    setCurrentJourney,
    setCurrentMission,
    isMature: isMature(journeys),
  }
}
